import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService, FileService, IStorage, LocalStorage } from '@isophi/core-legacy';
import { BaseComponent, IconService, ToastService } from '@isophi/core-ui';
import {
  Archive,
  ChildBulkExport,
  ChildGroupSuggestion,
  ChildProfileBulkDelete,
  ChildProfileList,
  ChildrenService,
  ClassesService,
  InvitationsService,
  InviteCloseRelatives,
} from '@isophi/mng-api';
import { PopUpComponent } from '@isophi/teachers-shared';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { debounceTime, finalize, map, Observable, Subscription, takeUntil, tap } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { UserService } from '../../../../core/services/user.service';
import { TransferChildrenModalComponent } from '../../../../shared/components/transfer-children-modal/transfer-children-modal.component';
import { EnrollmentHelperService } from '../../../../shared/services/enrollment-helper.service';
import { HandleErrorService } from '../../../../shared/services/handle-error.service';
import { LocalStorageConstService } from '../../../../shared/services/local-storage-const.service';
import { FilterCriteria } from './filter-criteria.model';

enum ChildMenuEnum {
  ARCHIVE = 'archive',
  UNARCHIVE = 'unarchive',
  REMOVE = 'remove',
  INVITE = 'invite',
  EXPORT = 'export',
  EXPORT_WITH_CONTACT = 'export_with_contact',
  TRANSFER = 'transfer',
}

const WITHOUT_CLASS = '__without_class__';

@Component({
  selector: 'app-children-filter',
  templateUrl: './children-filter.component.html',
  styleUrls: ['./children-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChildrenFilterComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChild('toastTemplate') toastTemplate: TemplateRef<any>;
  @ViewChild('toastTemplate2') toastTemplate2: TemplateRef<any>;

  infoMessage: string;

  infoMessage2: string;

  @Input() selectedChildren: ChildProfileList[] = [];

  @Output() filterUpdated: EventEmitter<FilterCriteria> = new EventEmitter();
  @Output() refreshChildren = new EventEmitter<void>();

  childrenGroups$: Observable<ChildGroupSuggestion[]>;
  childrenGroupsWithWithoutClass$: Observable<ChildGroupSuggestion[]>;

  selectedGroup: string;
  withoutClass = false;

  nameFilter: FormControl = new FormControl('');

  filteredChildren$: Observable<ChildProfileList[]>;

  archivedChildren = false;

  childMenuEnum = ChildMenuEnum;

  initChildMenu: FormControl<string | null>;

  private _subs: Subscription[] = [];

  private storage: IStorage;

  constructor(
    private classesService: ClassesService,
    public iconService: IconService,
    private router: Router,
    private links: RouterLinkFactory,
    private lSConstService: LocalStorageConstService,
    private translocoService: TranslocoService,
    private popUp: PopUpComponent,
    private childrenService: ChildrenService,
    private spinner: NgxSpinnerService,
    private toastr: ToastService,
    private invitationsService: InvitationsService,
    private handleErrorService: HandleErrorService,
    private http: HttpClient,
    private fileService: FileService,
    private enrollmentHelperService: EnrollmentHelperService,
    private modal: NgbModal,
    private authService: AuthService,
    private userService: UserService
  ) {
    super();
    this.childrenGroups$ = this.classesService.classesSuggestionsList().pipe(
      // we need to reset selected group if it doesnt match fetched, and let the parent list re-fetch
      tap((groups) => {
        if (!groups || groups.length === 0) {
          return;
        }

        const hadStaleGroup =
          !!this.selectedGroup && this.selectedGroup !== WITHOUT_CLASS && !groups.some((group) => group.uuid === this.selectedGroup);
        if (hadStaleGroup) {
          this.selectedGroup = null;
          this.storage.removeItem(this.lSConstService.childrenFilter);
          this.filterChildren();
        }
      })
    );
    this.childrenGroupsWithWithoutClass$ = this.childrenGroups$.pipe(
      map((groups) => [{ uuid: WITHOUT_CLASS, name: this.translocoService.translate('group/withoutClass') }, ...groups])
    );
    this.storage = new LocalStorage();
    this.initChildMenu = new FormControl(null);
  }

  ngOnInit(): void {
    const enrollmentSuccess = this.enrollmentHelperService.enrollmentSuccess;
    const currentWorkplaceUuid = this.userService.getCurrentKindergartenUUID();

    let filterCriteria: FilterCriteria | null = null;
    if (this.storage.hasItem(this.lSConstService.childrenFilter)) {
      const storageData = this.storage.getItem(this.lSConstService.childrenFilter);
      if (storageData && storageData !== 'undefined') {
        const parsedFilterCriteria: FilterCriteria = JSON.parse(storageData);
        if (parsedFilterCriteria.workplaceUuid === currentWorkplaceUuid) {
          filterCriteria = parsedFilterCriteria;
        } else {
          this.storage.removeItem(this.lSConstService.childrenFilter);
        }
      }
    }

    if (filterCriteria) {
      this.nameFilter.patchValue(filterCriteria.name);
      if (enrollmentSuccess) {
        this.selectedGroup = null;
        this.withoutClass = false;
      } else {
        this.selectedGroup = filterCriteria.group;
        this.withoutClass = !!filterCriteria.withoutClass;
      }
      if (this.withoutClass) {
        this.selectedGroup = WITHOUT_CLASS;
      }

      this.archivedChildren = filterCriteria.archived;
      this.filterChildren();
    } else {
      if (enrollmentSuccess) {
        this.selectedGroup = null;
        this.withoutClass = false;
        this.enrollmentHelperService.enrollmentSuccess = false;
      }
    }

    this._subs.push(
      this.nameFilter.valueChanges
        .pipe(
          debounceTime(300) // Adjust the delay time as needed (e.g., 300ms)
        )
        .subscribe((name) => {
          const childName = name.trim().toLowerCase();
          this.filterChildren(childName);
        })
    );
  }

  createChild() {
    this.router.navigate(this.links.createChild());
  }

  filterChildren(name?: string): void {
    const resolvedGroup = this.withoutClass ? null : this.selectedGroup;
    const filterCriteria: FilterCriteria = {
      group: resolvedGroup,
      withoutClass: this.withoutClass,
      name: name || this.nameFilter.value.trim().toLowerCase(),
      archived: this.archivedChildren,
      workplaceUuid: this.userService.getCurrentKindergartenUUID(),
    };
    this.storage.setItem(this.lSConstService.childrenFilter, JSON.stringify(filterCriteria));
    this.filterUpdated.emit(filterCriteria);
  }

  onGroupChange(value: string | null): void {
    const resolvedValue = value as string | null;

    if (resolvedValue === WITHOUT_CLASS) {
      this.selectedGroup = WITHOUT_CLASS;
      this.withoutClass = true;
    } else {
      this.selectedGroup = resolvedValue;
      this.withoutClass = false;
    }
    this.filterChildren();
  }

  onOptionSelected(option): void {
    this.initChildMenu.patchValue(this.translocoService.translate('child/select'));

    if (option === this.childMenuEnum.EXPORT) {
      this.simpleExport(this.selectedChildren);
    } else if (option === this.childMenuEnum.EXPORT_WITH_CONTACT) {
      this.exportWithContact(this.selectedChildren);
    } else if (option === this.childMenuEnum.ARCHIVE || option === this.childMenuEnum.UNARCHIVE) {
      this.toggleArchiveChildren(this.selectedChildren, option === this.childMenuEnum.ARCHIVE);
    } else if (option === this.childMenuEnum.REMOVE) {
      this.removeChildren(this.selectedChildren);
    } else if (option === this.childMenuEnum.INVITE) {
      this.inviteParents(this.selectedChildren);
    } else if (option === this.childMenuEnum.TRANSFER) {
      this.openTransferModal(this.selectedChildren);
    }
  }

  canTransferWorkplace(): boolean {
    const teacher = this.authService.loggedUser?.teacher;
    const hasAccess = !!teacher?.isDirector?.();
    const hasMultipleWorkplaces = (teacher?.kindergartens || []).length > 1;
    return hasAccess && hasMultipleWorkplaces;
  }

  openTransferModal(selectedChildren: ChildProfileList[]): void {
    const modalRef = this.modal.open(TransferChildrenModalComponent, { size: 'md', centered: true });
    modalRef.componentInstance.selectedChildrenCount = selectedChildren.length;
    modalRef.componentInstance.currentWorkplaceUuid = this.authService.loggedUser?.teacher?.kindergartens?.[0]?.uuid || null;

    modalRef.result
      .then((result) => {
        if (!result?.workplaceUuid) return;
        const payload = {
          childUuids: selectedChildren.map((child) => child.id),
          workplaceUuid: result.workplaceUuid,
          classUuid: result.classUuid ?? null,
        };

        this.spinner.show();
        this.childrenService
          .childrenTransferToWorkplaceCreate(payload)
          .pipe(finalize(() => this.spinner.hide()))
          .subscribe({
            next: (response) => {
              this.toastr.success(this.translocoService.translate('transfer/childrenMovedSuccess', { workplace: response?.workplaceName }));
              this.refreshChildren.emit();
            },
            error: () => {
              this.toastr.error(this.translocoService.translate('transfer/childrenMovedError'));
            },
          });
      })
      .catch(() => null);
  }

  async toggleArchiveChildren(selectedChildren: ChildProfileList[], archive: boolean): Promise<void> {
    const title = this.translocoService.translate(archive ? 'child/archiveChildren' : 'child/unarchiveChildren');
    const text = this.translocoService.translate('shared/confirm');
    const yesText = this.translocoService.translate('shared/YES');
    const noText = this.translocoService.translate('shared/NO');
    const confirm = await this.popUp.confirm(title, text, null, yesText, noText, 'btn-primary', 'btn-outline-primary');

    if (!confirm) return;

    await this.spinner.show();

    const childrenArchive: Archive = {
      childUuids: selectedChildren.map((child) => child.id),
    };

    const apiCall$ = archive
      ? this.childrenService.childrenBulkArchiveCreate(childrenArchive)
      : this.childrenService.childrenBulkArchiveRestoreCreate(childrenArchive);

    apiCall$
      .pipe(
        finalize(() => this.spinner.hide()),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe({
        next: () => {
          this.toastr.success(
            archive
              ? this.translocoService.translate('child/archiveSuccessToast')
              : this.translocoService.translate('child/unarchiveSuccessToast')
          );
          this.refreshChildren.emit();
        },
        error: (e) => {
          this.handleErrorService.handleFormError(
            e,
            archive ? 'child/archiveChildren' : 'child/unarchiveChildren',
            archive ? 'child/archiveErrorToast' : 'child/unarchiveErrorToast'
          );
        },
      });
  }

  async removeChildren(selectedChildren: ChildProfileList[]): Promise<void> {
    const title = this.translocoService.translate('child/removeChildren');
    const text = this.translocoService.translate('shared/confirm');
    const yesText = this.translocoService.translate('shared/YES');
    const noText = this.translocoService.translate('shared/NO');
    const confirm = await this.popUp.confirm(title, text, null, yesText, noText, 'btn-primary', 'btn-outline-primary');

    if (!confirm) return;

    await this.spinner.show();

    const data: ChildProfileBulkDelete = {
      childUuids: selectedChildren.map((child) => child.id),
    };

    this.childrenService
      .childrenBulkDeleteCreate(data)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translocoService.translate('child/removeChildrenSuccess'),
            this.translocoService.translate('child/removeChildren')
          );
          this.refreshChildren.emit();
        },
        error: (e) => {
          this.handleErrorService.handleFormError(e, 'child/removeChildren', 'child/removeChildrenFailed');
        },
      });
  }

  inviteParents(selectedChildren: ChildProfileList[]): void {
    this.spinner.show();

    const data: InviteCloseRelatives = {
      childUuids: selectedChildren.map((child) => child.id),
    };

    this.invitationsService
      .invitationsInviteCloseRelativesCreate(data)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: (val) => {
          if (val.childUuidsWithoutLegalRepresentativeEmail.length > 0 || val.childUuidsWithoutLegalRepresentative.length > 0) {
            if (val.childUuidsWithoutLegalRepresentativeEmail.length > 0) {
              const childrenWithoutMails = selectedChildren
                .filter((child) => val.childUuidsWithoutLegalRepresentativeEmail.includes(child.id))
                .map((child) => child.lastName + ' ' + child.firstName)
                .join(', ');
              this.infoMessage = `${this.translocoService.translate('child/parentsWithoutMailsError')}:<br> ${childrenWithoutMails}`;
              this.toastr.error(this.toastTemplate, '', { autohide: false });
            }
            if (val.childUuidsWithoutLegalRepresentative.length > 0) {
              const childrenWithoutMails = selectedChildren
                .filter((child) => val.childUuidsWithoutLegalRepresentative.includes(child.id))
                .map((child) => child.lastName + ' ' + child.firstName)
                .join(', ');
              this.infoMessage2 = `${this.translocoService.translate(
                'child/parentsWithoutLegalRepresentativeError'
              )}:<br> ${childrenWithoutMails}`;
              this.toastr.error(this.toastTemplate2, '', { autohide: false });
            }

            const successChildUuids = selectedChildren
              .filter((child) => !val.childUuidsWithoutLegalRepresentativeEmail.some((uuid) => uuid === child.id))
              .filter((child) => !val.childUuidsWithoutLegalRepresentative.some((uuid) => uuid === child.id));

            if (successChildUuids.length > 0) {
              this.toastr.success(
                this.translocoService.translate('child/invitationPartialSuccess'),
                this.translocoService.translate('child/inviteParent')
              );
              this.refreshChildren.emit();
            }
          } else {
            this.toastr.success(
              this.translocoService.translate('child/inviteParentSuccess'),
              this.translocoService.translate('child/inviteParent')
            );
            this.refreshChildren.emit();
          }
        },
        error: (e) => {
          this.handleErrorService.handleFormError(e, 'child/inviteParent', 'child/inviteParentError');
        },
      });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subs.forEach((_sub) => _sub.unsubscribe());
  }

  simpleExport(selectedChildren: ChildProfileList[]): void {
    this.spinner.show();

    const data: ChildBulkExport = {
      childUuids: selectedChildren.map((child) => child.id),
    };

    const url = environment.api + `/children/bulk-export/`;

    this.http
      .post(url, data, { responseType: 'blob' })
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: (val) => {
          this.fileService.downloadFile(
            val,
            `${this.translocoService.translate('file/list')}.xlsx`,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          );
          this.toastr.success(
            this.translocoService.translate('child/simpleExportSuccess'),
            this.translocoService.translate('child/simpleExport')
          );
        },
        error: (e) => {
          this.handleErrorService.handleFormError(e, 'child/simpleExport', 'child/simpleExportError');
        },
      });
  }

  exportWithContact(selectedChildren: ChildProfileList[]): void {
    this.spinner.show();

    const data: ChildBulkExport = {
      childUuids: selectedChildren.map((child) => child.id),
    };

    const url = environment.api + `/children/bulk-export-with-contact/`;

    this.http
      .post(url, data, { responseType: 'blob' })
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: (val) => {
          this.fileService.downloadFile(
            val,
            `${this.translocoService.translate('file/tableWithContacts')}.xlsx`,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          );
          this.toastr.success(
            this.translocoService.translate('child/tableWithContactsExportSuccess'),
            this.translocoService.translate('child/tableWithContactsExport')
          );
        },
        error: (e) => {
          this.handleErrorService.handleFormError(e, 'child/tableWithContactsExport', 'child/tableWithContactsExportError');
        },
      });
  }
}
